import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Divider,
  ListSubheader,
} from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PeopleIcon from '@mui/icons-material/People';
import SettingsIcon from '@mui/icons-material/Settings';
import AssessmentIcon from '@mui/icons-material/Assessment';
import DashboardIcon from '@mui/icons-material/Dashboard';
import HomeIcon from '@mui/icons-material/Home';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import { useNavigate, useLocation } from 'react-router-dom';

const drawerWidth = 300;

const listItemButtonSx = {
  mx: 1.5,
  my: 0.5,
  borderRadius: '10px',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  color: 'text.secondary',
  '&:hover': {
    color: '#D2B35C',
    backgroundColor: 'rgba(210, 179, 92, 0.08)',
    transform: 'translateX(4px)',
    '& .MuiListItemIcon-root': {
      color: '#D2B35C',
      transform: 'scale(1.1)',
    },
  },
  '&.Mui-selected': {
    color: '#D2B35C',
    backgroundColor: 'rgba(210, 179, 92, 0.15)',
    backdropFilter: 'blur(4px)',
    borderLeft: '4px solid #D2B35C',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.15), inset 0 0 12px rgba(210, 179, 92, 0.12)',
    '&:hover': {
      backgroundColor: 'rgba(210, 179, 92, 0.2)',
    },
    '& .MuiListItemIcon-root': {
      color: '#D2B35C',
      filter: 'drop-shadow(0 0 6px rgba(210, 179, 92, 0.6))',
    },
    '& .MuiListItemText-primary': {
      fontWeight: 700,
      color: '#D2B35C',
    },
  },
  '& .MuiListItemIcon-root': {
    color: 'text.secondary',
    minWidth: 40,
    transition: 'all 0.3s ease',
  },
};

function Sidebar({ onSelectMenu, menuSelecionado }) {
  const navigate = useNavigate();
  const location = useLocation();

  const isMenuActive = (menu) => {
    if (menu === 'Início' && location.pathname === '/home') return true;
    if (menu === 'Dashboard' && location.pathname === '/dashboard') return true;
    if (menu === 'Vendas' && location.pathname === '/vendas') return true;
    if (menu === 'Lista de Corretores' && location.pathname === '/corretores') return true;
    if (menu === 'Relatório' && location.pathname === '/relatorio') return true;
    if (menu === 'Comparativo' && location.pathname === '/comparativo') return true;
    if (menu === 'Apresentação' && location.pathname === '/apresentacao') return true;
    if (menu === 'Configurações' && location.pathname === '/configuracoes') return true;
    return false;
  };

  const handleMenuClick = (menu) => {
    if (menu === 'Início') {
      navigate('/home');
      return;
    }
    if (menu === 'Dashboard') {
      navigate('/dashboard');
      return;
    }
    if (menu === 'Vendas') {
      navigate('/vendas');
      return;
    }
    if (menu === 'Lista de Corretores') {
      navigate('/corretores');
      return;
    }
    if (menu === 'Relatório') {
      navigate('/relatorio');
      return;
    }
    if (menu === 'Comparativo') {
      navigate('/comparativo');
      return;
    }
    if (menu === 'Apresentação') {
      navigate('/apresentacao');
      return;
    }
    if (menu === 'Configurações') {
      navigate('/configuracoes');
      return;
    }
    onSelectMenu(menu);
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          borderRight: '1px solid rgba(210, 179, 92, 0.2)',
          boxShadow: '4px 0 20px rgba(0, 0, 0, 0.15)',
        },
      }}
    >
      <Box sx={{ p: 2, textAlign: 'center', borderBottom: '1px solid rgba(210, 179, 92, 0.15)' }}>
        <Box
          component="img"
          src="/logobranca.png"
          alt="Logo Prime Class"
          sx={{
            height: 50,
            width: 'auto',
            display: 'block',
            mx: 'auto',
            mb: 1,
          }}
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
      </Box>
      <Divider sx={{ borderColor: 'rgba(210, 179, 92, 0.15)' }} />

      <List sx={{ flexGrow: 1, py: 1, overflowY: 'auto' }}>
        {/* Categoria 1: Navegação Principal */}
        <ListSubheader
          sx={{
            bgcolor: 'transparent',
            color: '#D2B35C',
            fontWeight: 700,
            fontSize: '0.72rem',
            letterSpacing: '1px',
            textTransform: 'uppercase',
            px: 2.5,
            pt: 1.5,
            pb: 0.5,
            lineHeight: '18px',
          }}
        >
          Navegação Principal
        </ListSubheader>

        <ListItem disablePadding>
          <ListItemButton
            sx={listItemButtonSx}
            selected={isMenuActive('Início')}
            onClick={() => handleMenuClick('Início')}
          >
            <ListItemIcon>
              <HomeIcon />
            </ListItemIcon>
            <ListItemText primary="Início" />
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding>
          <ListItemButton
            sx={listItemButtonSx}
            selected={isMenuActive('Dashboard')}
            onClick={() => handleMenuClick('Dashboard')}
          >
            <ListItemIcon>
              <DashboardIcon />
            </ListItemIcon>
            <ListItemText 
              primary={
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.6 }}>
                  <span>Dashboard</span>
                  <Box 
                    component="span" 
                    sx={{ 
                      fontSize: '0.72rem', 
                      opacity: 0.8, 
                      fontWeight: 400,
                    }}
                  >
                    (Modo TV)
                  </Box>
                </Box>
              } 
            />
          </ListItemButton>
        </ListItem>

        <Divider sx={{ my: 1, borderColor: 'rgba(210, 179, 92, 0.1)' }} />

        {/* Categoria 2: Gestão & Vendas */}
        <ListSubheader
          sx={{
            bgcolor: 'transparent',
            color: '#D2B35C',
            fontWeight: 700,
            fontSize: '0.72rem',
            letterSpacing: '1px',
            textTransform: 'uppercase',
            px: 2.5,
            pt: 1,
            pb: 0.5,
            lineHeight: '18px',
          }}
        >
          Gestão & Vendas
        </ListSubheader>

        <ListItem disablePadding>
          <ListItemButton
            sx={listItemButtonSx}
            selected={isMenuActive('Vendas') || (menuSelecionado && menuSelecionado.startsWith('VGV'))}
            onClick={() => handleMenuClick('Vendas')}
          >
            <ListItemIcon>
              <AttachMoneyIcon />
            </ListItemIcon>
            <ListItemText primary="Vendas" />
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding>
          <ListItemButton
            sx={listItemButtonSx}
            selected={isMenuActive('Lista de Corretores')}
            onClick={() => handleMenuClick('Lista de Corretores')}
          >
            <ListItemIcon>
              <PeopleIcon />
            </ListItemIcon>
            <ListItemText primary="Lista de Corretores" />
          </ListItemButton>
        </ListItem>

        <Divider sx={{ my: 1, borderColor: 'rgba(210, 179, 92, 0.1)' }} />

        {/* Categoria 3: Análises & Relatórios */}
        <ListSubheader
          sx={{
            bgcolor: 'transparent',
            color: '#D2B35C',
            fontWeight: 700,
            fontSize: '0.72rem',
            letterSpacing: '1px',
            textTransform: 'uppercase',
            px: 2.5,
            pt: 1,
            pb: 0.5,
            lineHeight: '18px',
          }}
        >
          Análises & Relatórios
        </ListSubheader>

        <ListItem disablePadding>
          <ListItemButton
            sx={listItemButtonSx}
            selected={isMenuActive('Relatório')}
            onClick={() => handleMenuClick('Relatório')}
          >
            <ListItemIcon>
              <AssessmentIcon />
            </ListItemIcon>
            <ListItemText primary="Relatório" />
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding>
          <ListItemButton
            sx={listItemButtonSx}
            selected={isMenuActive('Comparativo')}
            onClick={() => handleMenuClick('Comparativo')}
          >
            <ListItemIcon>
              <CompareArrowsIcon />
            </ListItemIcon>
            <ListItemText primary="Comparativo" />
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding>
          <ListItemButton
            sx={listItemButtonSx}
            selected={isMenuActive('Apresentação')}
            onClick={() => handleMenuClick('Apresentação')}
          >
            <ListItemIcon>
              <SlideshowIcon />
            </ListItemIcon>
            <ListItemText primary="Apresentação" />
          </ListItemButton>
        </ListItem>

        <Divider sx={{ my: 1, borderColor: 'rgba(210, 179, 92, 0.1)' }} />

        {/* Categoria 4: Sistema */}
        <ListSubheader
          sx={{
            bgcolor: 'transparent',
            color: '#D2B35C',
            fontWeight: 700,
            fontSize: '0.72rem',
            letterSpacing: '1px',
            textTransform: 'uppercase',
            px: 2.5,
            pt: 1,
            pb: 0.5,
            lineHeight: '18px',
          }}
        >
          Sistema
        </ListSubheader>

        <ListItem disablePadding>
          <ListItemButton
            sx={listItemButtonSx}
            selected={isMenuActive('Configurações')}
            onClick={() => handleMenuClick('Configurações')}
          >
            <ListItemIcon>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText primary="Configurações" />
          </ListItemButton>
        </ListItem>
      </List>
    </Drawer>
  );
}

export default Sidebar;


